import { useState, useEffect, useCallback } from 'react';
import { fetchCurrentPrices } from '../utils/api/coingecko';
import { fallbackData } from '../utils/fallbackData';
import { ERROR_MESSAGES } from '../utils/api/constants';
import { toast } from 'react-hot-toast';
import { useOnlineStatus } from './useOnlineStatus';

// Price data cache
let pricesCache = fallbackData;
let lastPriceUpdate = 0;
const PRICE_CACHE_TTL = 60000; // 1 minute

export function useCryptoPrices() {
  const [prices, setPrices] = useState(pricesCache);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(lastPriceUpdate ? new Date(lastPriceUpdate) : null);
  const isOnline = useOnlineStatus();

  const refresh = useCallback(async (showToast = false) => {
    try {
      setIsLoading(true);
      
      if (!isOnline) {
        console.debug('Offline, using cached data');
        setPrices(pricesCache);
        setError(ERROR_MESSAGES.OFFLINE);
        if (showToast) {
          toast.error(ERROR_MESSAGES.OFFLINE);
        }
        return;
      }

      // Check if cache is still valid
      if (!showToast && Date.now() - lastPriceUpdate < PRICE_CACHE_TTL) {
        setPrices(pricesCache);
        setLastUpdate(new Date(lastPriceUpdate));
        setError(null);
        return;
      }

      console.debug('Fetching fresh price data...');
      const data = await fetchCurrentPrices(true);
      
      // Validate the data
      const hasValidData = Object.values(data).every(coin => 
        typeof coin.price === 'number' && 
        !isNaN(coin.price) && 
        coin.price > 0
      );

      if (hasValidData) {
        console.debug('Setting new price data:', data);
        setPrices(data);
        pricesCache = data;
        lastPriceUpdate = Date.now();
        setLastUpdate(new Date());
        setError(null);
        if (showToast) {
          toast.success('Prices updated successfully');
        }
      } else {
        console.warn('Invalid data received:', data);
        throw new Error('Invalid data received');
      }
    } catch (err: any) {
      console.warn('Price fetch error:', err);
      // Keep existing prices but show error
      setPrices(pricesCache);
      setError(err.message || ERROR_MESSAGES.API_ERROR);
      if (showToast) {
        toast.error(err.message || ERROR_MESSAGES.API_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isOnline]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(() => {
      refresh();
    }, PRICE_CACHE_TTL);

    return () => clearInterval(interval);
  }, [refresh, isOnline]);

  return { 
    prices, 
    isLoading, 
    error, 
    refresh,
    lastUpdate 
  };
}